import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtDate, exportCSV, can } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Select, Textarea, Empty, StatusBadge,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, DownloadSimple, Eye, Printer, Receipt, Trash, Calculator, PaperPlaneTilt } from "@phosphor-icons/react";
import SendEmailDialog from "@/components/SendEmailDialog";

function fmtUSD(n) {
  if (typeof n === "string") return n;
  return `$ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function ymd(d) { return d.toISOString().slice(0, 10); }

function firstOfMonth() {
  const d = new Date();
  return ymd(new Date(d.getFullYear(), d.getMonth(), 1));
}
function lastOfMonth() {
  const d = new Date();
  return ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

export default function Invoices() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(null);
  const [form, setForm] = useState({
    customer: "", period_start: firstOfMonth(), period_end: lastOfMonth(),
    tax_rate: 16, notes: "",
  });
  const [preview, setPreview] = useState(null);
  const [emailInv, setEmailInv] = useState(null);  // { inv, to, subject }
  const canManage = can(user.role, "manager");

  async function load() {
    try { const { data } = await api.get("/invoices"); setRows(data); }
    catch (e) { toast.error(formatError(e)); }
  }
  async function loadCustomers() {
    try {
      const { data } = await api.get("/customers?active_only=true");
      setCustomers(data);
    } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
  }
  useEffect(() => { load(); loadCustomers(); }, []);

  async function checkPreview(payload) {
    setPreview(null);
    try {
      const { data } = await api.get(
        `/invoices/preview/eligible?customer=${encodeURIComponent(payload.customer)}&period_start=${payload.period_start}&period_end=${payload.period_end}`
      );
      setPreview(data);
    } catch (e) { toast.error(formatError(e)); }
  }

  async function generate(e) {
    e.preventDefault();
    try {
      await api.post("/invoices/generate", {
        customer: form.customer,
        period_start: new Date(form.period_start).toISOString(),
        period_end: new Date(form.period_end).toISOString(),
        tax_rate: Number(form.tax_rate || 0),
        notes: form.notes,
      });
      toast.success("Invoice generated");
      setGenOpen(false); setPreview(null); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function setStatus(inv, status) {
    try {
      await api.patch(`/invoices/${inv.id}/status?status=${status}`);
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function remove(inv) {
    if (!window.confirm(`Delete invoice ${inv.number}? This will release its delivery notes for re-invoicing.`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid="invoices-page">
      <PageHeader
        overline="Module / Billing"
        title="Invoices"
        subtitle="Auto-generate month-end invoices from confirmed delivery notes — one click per customer."
        actions={
          <>
            <BtnSecondary onClick={() => exportCSV(rows, `invoices-${Date.now()}.csv`)}>
              <DownloadSimple size={14} weight="bold" className="inline mr-1.5" /> CSV
            </BtnSecondary>
            {canManage && (
              <BtnPrimary onClick={() => { setForm({ customer: customers[0]?.name || "", period_start: firstOfMonth(), period_end: lastOfMonth(), tax_rate: 16, notes: "" }); setGenOpen(true); setPreview(null); }}
                data-testid="invoice-generate">
                <Calculator size={14} weight="bold" className="inline mr-1.5" /> Generate Invoice
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="phizo-card">
        {rows.length === 0 ? <div className="p-5"><Empty label="No invoices generated yet. Click 'Generate Invoice' once you have confirmed delivery notes." /></div> : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Number</th><th>Customer</th><th>Period</th>
                  <th className="text-right">Lines</th>
                  <th className="text-right">Total (USD)</th>
                  <th>Status</th><th>Created</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.number}`}>
                    <td className="mono font-semibold">{inv.number}</td>
                    <td className="font-medium">{inv.customer}</td>
                    <td className="text-sm text-[#52525b]">{fmtDate(inv.period_start)} → {fmtDate(inv.period_end)}</td>
                    <td className="text-right mono">{inv.lines?.length || 0}</td>
                    <td className="text-right mono font-semibold">{fmtUSD(inv.total_usd)}</td>
                    <td><StatusBadge status={inv.status === "void" ? "cancelled" : inv.status === "paid" ? "approved" : inv.status === "sent" ? "sent" : "draft"} /></td>
                    <td className="text-sm text-[#52525b]">{fmtDate(inv.created_at)}</td>
                    <td className="text-right whitespace-nowrap">
                      <Link to={`/invoices/${inv.id}/print`} target="_blank" rel="noreferrer">
                        <BtnGhost data-testid={`invoice-print-${inv.number}`}>
                          <Printer size={12} weight="bold" /> Print
                        </BtnGhost>
                      </Link>
                      <BtnGhost
                        data-testid={`invoice-email-${inv.number}`}
                        onClick={async () => {
                          try {
                            const { data } = await api.get(`/invoices/${inv.id}/email-payload`);
                            setEmailInv({ inv, to: data.to || "", subject: data.subject });
                            if (!data.has_email) {
                              toast.message("No email on file for this customer — type one manually.");
                            }
                          } catch (e) { toast.error(formatError(e)); }
                        }}>
                        <PaperPlaneTilt size={12} weight="bold" /> Email
                      </BtnGhost>
                      {canManage && inv.status === "draft" && (
                        <BtnGhost onClick={() => setStatus(inv, "sent")}>Mark Sent</BtnGhost>
                      )}
                      {canManage && inv.status === "sent" && (
                        <BtnGhost onClick={() => setStatus(inv, "paid")}>Mark Paid</BtnGhost>
                      )}
                      {user.role === "admin" && (
                        <BtnGhost onClick={() => remove(inv)} className="text-[#FF2A2A]">
                          <Trash size={12} weight="bold" />
                        </BtnGhost>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generate */}
      <Modal open={genOpen} onClose={() => setGenOpen(false)} title="Generate Invoice from Delivery Notes" wide>
        <form onSubmit={generate} className="space-y-4">
          <div className="phizo-surface p-3 text-sm">
            <div className="phizo-label mb-1">How it works</div>
            All <strong>confirmed</strong> delivery notes for the chosen customer & period that have not yet been invoiced will be bundled into a numbered invoice (INV-####) at USD rates from the delivery note.
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Customer">
              <Select required data-testid="inv-customer"
                value={form.customer}
                onChange={(e) => setForm({ ...form, customer: e.target.value })}>
                <option value="">Select customer…</option>
                {customers.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Period Start">
              <Input type="date" required data-testid="inv-start"
                value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
            </Field>
            <Field label="Period End">
              <Input type="date" required data-testid="inv-end"
                value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
            </Field>
          </div>
          <Field label="Tax Rate (%)">
            <Input type="number" step="0.01" min="0" value={form.tax_rate}
              onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} />
          </Field>
          <Field label="Notes / Reference">
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>

          <div className="flex items-center justify-between pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary type="button"
              onClick={() => checkPreview(form)}
              data-testid="inv-preview">
              Preview Eligible DNs
            </BtnSecondary>
            <div className="flex gap-2">
              <BtnSecondary type="button" onClick={() => setGenOpen(false)}>Cancel</BtnSecondary>
              <BtnPrimary type="submit" data-testid="inv-generate" disabled={!form.customer}>
                Generate Invoice
              </BtnPrimary>
            </div>
          </div>

          {preview && (
            <div className="phizo-surface p-3">
              <div className="flex items-center justify-between">
                <div className="phizo-label">Preview</div>
                <div className="text-sm">
                  <strong>{preview.count}</strong> DNs · <strong>{fmtUSD(preview.subtotal_usd)}</strong> subtotal
                </div>
              </div>
              {preview.delivery_notes.length === 0 ? (
                <div className="text-xs text-[#FF2A2A] mt-2">No un-invoiced confirmed delivery notes match.</div>
              ) : (
                <table className="phizo-table mt-2">
                  <thead><tr><th>DN</th><th>Date</th><th>Product</th><th className="text-right">Tonnes</th><th className="text-right">Value</th></tr></thead>
                  <tbody>
                    {preview.delivery_notes.map((d) => (
                      <tr key={d.id}>
                        <td className="mono text-xs">{d.number}</td>
                        <td className="text-xs">{fmtDate(d.delivery_date)}</td>
                        <td className="text-sm">{d.product_name}</td>
                        <td className="text-right mono">{d.tonnes}</td>
                        <td className="text-right mono">{fmtUSD(d.tonnes * d.rate_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </form>
      </Modal>

      <SendEmailDialog
        open={!!emailInv}
        onClose={() => setEmailInv(null)}
        title={emailInv ? `Email Invoice ${emailInv.inv.number}` : "Email"}
        endpoint={emailInv ? `/invoices/${emailInv.inv.id}/send-email` : ""}
        initialTo={emailInv?.to || ""}
        initialSubject={emailInv?.subject || ""}
        masterCcHint="team@phizoquarry.com"
        onSent={() => load()}
      />
    </div>
  );
}
